import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/src/lib/axios/axios';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import { OpenDmBody, ChannelResponse } from '../types/backend';

export type OpenDmDTO = {
  targetUserId: number;
  targetUserName?: string; // Optional: used to set channel name if backend doesn't provide it
};

const openDm = async (data: OpenDmDTO): Promise<ChannelResponse> => {
  const body: OpenDmBody = {
    'target-user-id': data.targetUserId,
  };

  const response: ChannelResponse = await axios.post(
    `${MESSAGING_API_BASE}/dm`,
    body
  );

  // If backend doesn't provide a name and we have the target user name, use it
  if (!response.name && data.targetUserName) {
    response.name = data.targetUserName;
  }

  return response;
};

export const useOpenDm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OpenDmDTO) => openDm(data),
    onSuccess: (channelResponse) => {
      // Update the channels cache with the full channel data
      // This ensures the DM has proper member info before the list refetches
      const channelKeys = messagingQueryKeys.channels();

      queryClient.setQueriesData({ queryKey: channelKeys }, (oldData: any) => {
        if (!oldData?.pages) return oldData;

        // Update the channel in the cached pages if it exists
        const updatedPages = oldData.pages.map((page: any) => {
          const rows = page.rows || [];
          const existingIndex = rows.findIndex(
            (ch: any) => ch.id === channelResponse.id
          );

          if (existingIndex >= 0) {
            // Update existing channel with full data (preserves member info)
            const updated = [...rows];
            updated[existingIndex] = channelResponse;
            return { ...page, rows: updated };
          }

          return page;
        });

        return { ...oldData, pages: updatedPages };
      });

      // Invalidate to refetch and ensure we have latest data
      queryClient.invalidateQueries({ queryKey: channelKeys });
    },
  });
};
